// src/components/Students/StudentDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Paper,
  useTheme,
  alpha
} from '@mui/material';

const StudentDashboard = () => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [totalStudents, setTotalStudents] = useState(0);
  const [loading, setLoading] = useState(true);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch real student count
  useEffect(() => {
    const fetchStudentCount = async () => {
      try {
        const response = await axios.get('/students/api/list/');
        setTotalStudents(response.data.length);
      } catch (error) {
        console.error('Error fetching student count:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStudentCount();
  }, []);

  const managementActions = [
    { icon: '📤', title: 'Upload Students', description: 'Bulk upload student data from CSV/Excel', link: '/students/upload-students' },
    { icon: '➕', title: 'Add Student', description: 'Add individual student manually', link: '/students/add' },
    { icon: '📋', title: 'List Students', description: 'View and manage all students', link: '/students/list' },
    { icon: '🏠', title: 'Upload Homeroom Teachers', description: 'Assign homeroom teachers to students', link: '/students/upload-homeroom' }
  ];


  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            p: 3,
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    fontSize: '2rem'
                  }}
                >
                  📊
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Student Management Dashboard
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    icon={<span>🏫</span>}
                    label="Admin Portal"
                    sx={{ bgcolor: alpha(theme.palette.common.white, 0.2), color: 'white' }}
                  />
                  <Chip
                    icon={<span>👥</span>}
                    label="Student Management"
                    sx={{ bgcolor: alpha(theme.palette.common.white, 0.2), color: 'white' }}
                  />
                </Box>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {currentTime.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          {/* Decorative background elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.common.white, 0.1),
              zIndex: 0
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.common.white, 0.05),
              zIndex: 0
            }}
          />
        </Paper>
      </Box>

      {/* Single Total Students Stat */}
      <Grid container justifyContent="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h3" color="primary" fontWeight="bold">
                {loading ? '...' : totalStudents.toLocaleString()}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Active Students
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Main Content - Full Width */}
        <Grid item xs={12}>

          {/* Management Actions */}
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>⚙️</span>
                Student Management Tools
              </Typography>
              <Grid container spacing={2}>
                {managementActions.map((action, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      component={Link}
                      to={action.link}
                      elevation={0}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        border: `1px solid ${theme.palette.divider}`,
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'block',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[4]
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          {action.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {action.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {action.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StudentDashboard;
